import type { FreeLayoutPluginContext, WorkflowNodeEntity } from '@flowgram.ai/free-layout-editor';
import { nanoid } from 'nanoid';
import type { FlowNodeData } from '$/domains/workFlowV3/type';
import { useLanguageStore } from '$/store/languageStore';
import useFlowNextStore from '$/store/useTeamFlowStore';
import { getAllNodesBounds } from '$/utils/nodeAddPosition';
import {
  NODE_NAME_CONFIG,
  NODE_TYPE_TO_MODEL_TYPE_MAP,
  TVFlowNodeNextTypeEnum,
  type ModelNodeType,
} from '../config/node.config';
import {
  createDefaultDirectorConsoleState,
  createDirectorConsoleDataPatch,
} from '../DirectorConsole/state';
import type { DirectorConsoleNodeData } from '../DirectorConsole/types';
import { autoMatchAndUpdateNode } from './autoMatchModelTool';
import { getInitialNodeMcpData } from './modelConfig';
import { inheritCommonMcpInfo } from './ModelMcpInfo';
import { getNextNodeNameByCurrentType } from './nodeNameCounter';

export interface TextNodeMediaItem extends Record<string, any> {
  task_id?: string;
  url?: string;
  value?: string;
  watermark_url?: string;
  cover_url?: string;
  watermark_cover_url?: string;
  width?: number;
  height?: number;
  duration?: number;
}

export interface TextNodeMcpInfo extends Record<string, any> {
  api_name: string;
  model_id?: string;
  node_model_id?: string;
  model_name?: string;
  parameters?: Record<string, any>;
}

export interface TextNodeData extends Omit<FlowNodeData<TextNodeMediaItem>, 'mcpInfo' | 'media_list'> {
  title?: string;
  action?: string;
  media_list?: TextNodeMediaItem[];
  mcpInfo?: TextNodeMcpInfo;
  taskInfo?: Record<string, any>;
  tool?: Record<string, any>;
}

/** 文本节点创建入参的 data 与 {@link ScriptNodeData} 一致。 */
export type ScriptNodeData = TextNodeData;

interface MeidaCommonItem {
  task_id?: string;
  sub_task_id?: string;
  block_id?: string;
  fileByte?: number;
  material_id?: string;
  assetId?: number;
}

interface ImageMediaItem extends MeidaCommonItem {
  url: string;
  width: number;
  height: number;
  watermark_url: string;
  source?: 'upload' | 'generation' | 'paste' | 'material';
}

export interface ImageNodeData extends FlowNodeData {
  media_list: ImageMediaItem[];
  /** 本地上传与系统产物节点创建阶段的图片加载状态。 */
  status?: 'empty' | 'uploading' | 'loaded' | 'error';
  /** 系统生成图片的来源与编辑任务元数据。 */
  tool?: Record<string, unknown>;
}

interface VideoMediaItem extends MeidaCommonItem {
  url: string;
  width: number;
  height: number;
  watermark_url: string;
  compress_url?: string; // 压缩视频url
  watermark_compress_url?: string; // 压缩视频水印url
  duration: number;
  cover_url: string;
  watermark_cover_url: string;
}

export interface VideoNodeData extends FlowNodeData {
  media_list: VideoMediaItem[];
}

interface AudioMediaItem extends MeidaCommonItem {
  url: string;
  duration: number;
}

export interface AudioNodeData extends FlowNodeData {
  media_list: AudioMediaItem[];
}

export interface ToLineItem {
  toNodeId: string;
  toPortId: 'input' | 'output';
}

interface AddNodeParams<T> {
  /** 未同时传入 x、y 时使用画布默认落点（可视区域中心防重叠） */
  x?: number;
  y?: number;
  data: T;
  toLines?: ToLineItem[];
  /** 新节点命名锚点，连线面板会基于当前节点延续同类型计数。 */
  currentNode?: WorkflowNodeEntity;
  /** 资产库资源可显式指定节点名；未传时继续使用计数器。 */
  nodeName?: string;
}

/**
 * 判断是否为可深度合并的 plain object（排除数组与 null）。
 */
function isMergeablePlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * 深度合并：incoming 字段优先，defaults 仅补齐缺失或未传入的嵌套字段。
 */
function deepMergeIncomingOverDefaults(
  defaults: Record<string, unknown>,
  incoming: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...defaults };

  Object.entries(incoming).forEach(([key, incomingValue]) => {
    if (incomingValue === undefined) {
      return;
    }

    const defaultValue = result[key];
    if (isMergeablePlainObject(defaultValue) && isMergeablePlainObject(incomingValue)) {
      result[key] = deepMergeIncomingOverDefaults(defaultValue, incomingValue);
      return;
    }

    result[key] = incomingValue;
  });

  return result;
}

/**
 * 非用户上传节点时，按节点类型写入默认模型配置；
 * 若已携带 mcpInfo（如 Agent 落画布），与默认值做嵌套合并而非直接跳过。
 * 特效模板节点（isEffectNode）保留调用方 mcpInfo，不合并默认 parameters。
 */
function applyDefaultMcpInfoIfNeeded<T extends { mcpInfo?: unknown; isCustomer?: boolean; isEffectNode?: boolean; }>(
  data: T,
  nodeType: ModelNodeType,
): T {
  if (data.isCustomer || data.isEffectNode) {
    return data;
  }

  const modelType = NODE_TYPE_TO_MODEL_TYPE_MAP[nodeType];
  const { modelConfig } = useFlowNextStore.getState();

  const initialMcpData = getInitialNodeMcpData(modelConfig?.[modelType]);

  if (!initialMcpData || Object.keys(initialMcpData).length === 0) {
    return data;
  }

  return deepMergeIncomingOverDefaults(
    initialMcpData as Record<string, unknown>,
    data as Record<string, unknown>,
  ) as T;
}

/**
 * 判断创建节点时调用方是否显式传入了 mcpInfo。
 *
 * @param {object} data
 * @return {boolean}
 */
function hasIncomingMcpInfo(data: { mcpInfo?: unknown; }): boolean {
  return Object.prototype.hasOwnProperty.call(data, 'mcpInfo') && data.mcpInfo !== undefined;
}

interface ResolveCreateNodeMcpDataResult {
  nodeData: Record<string, unknown>;
  shouldInheritCommonMcpInfo: boolean;
}

/**
 * 按是否传入 mcpInfo 决定创建节点的模型初始化策略。
 * 传入 mcpInfo 时与默认配置合并；未传入时留空，待节点创建后继承公共 mcpInfo。
 *
 * @param {object} data
 * @param {TVFlowNodeNextTypeEnum} nodeType
 * @return {ResolveCreateNodeMcpDataResult}
 */
function resolveCreateNodeMcpData<T extends { mcpInfo?: unknown; isCustomer?: boolean; isEffectNode?: boolean; }>(
  data: T,
  nodeType: TVFlowNodeNextTypeEnum,
): ResolveCreateNodeMcpDataResult {
  const MCP_INIT_NODE_TYPES = new Set<TVFlowNodeNextTypeEnum>(
    Object.keys(NODE_TYPE_TO_MODEL_TYPE_MAP) as TVFlowNodeNextTypeEnum[],
  );

  if (!MCP_INIT_NODE_TYPES.has(nodeType) || data.isCustomer || data.isEffectNode) {
    return {
      nodeData: data as Record<string, unknown>,
      shouldInheritCommonMcpInfo: false,
    };
  }

  if (hasIncomingMcpInfo(data)) {
    return {
      nodeData: applyDefaultMcpInfoIfNeeded(data, nodeType as ModelNodeType) as Record<string, unknown>,
      shouldInheritCommonMcpInfo: false,
    };
  }

  return {
    nodeData: data as Record<string, unknown>,
    shouldInheritCommonMcpInfo: true,
  };
}

/**
 * 新建节点未传入 mcpInfo 时，基于公共模型配置继承 mcpInfo 并写回节点表单。
 *
 * @param {WorkflowNodeEntity} node
 * @param {TVFlowNodeNextTypeEnum} nodeType
 */
function applyInheritedCommonMcpInfo(
  node: WorkflowNodeEntity,
  nodeType: TVFlowNodeNextTypeEnum,
): void {
  const modelType = NODE_TYPE_TO_MODEL_TYPE_MAP[nodeType as ModelNodeType];
  const { modelConfig, commonModelMcpInfoMap } = useFlowNextStore.getState();
  const targetModelId = commonModelMcpInfoMap[nodeType as ModelNodeType]?.model_id
    ?? getInitialNodeMcpData(modelConfig?.[modelType])?.mcpInfo?.model_id
    ?? '';
  const inheritedMcpInfo = inheritCommonMcpInfo({
    node,
    targetModelId,
  });

  if (!inheritedMcpInfo || !node.form) {
    return;
  }

  const currentData = (node.toJSON()?.data ?? {}) as Record<string, unknown>;
  node.form.updateFormValues({ ...currentData, mcpInfo: inheritedMcpInfo });
}

/**
 * 在画布上创建节点并建立连线。
 */
function createWorkflowNode<T>(
  ctx: FreeLayoutPluginContext,
  nodeType: TVFlowNodeNextTypeEnum,
  {
    x,
    y,
    data,
    toLines,
    currentNode,
    nodeName: preferredNodeName,
  }: AddNodeParams<T>,
) {
  if (!ctx?.document) {
    console.warn('[createWorkflowNode] canvas context is not ready');
    return undefined;
  }

  const id = nanoid();
  const position = typeof x === 'number' && typeof y === 'number'
    ? { x, y }
    : ctx.document.getNodeDefaultPosition(nodeType);
  const { nodeData: resolvedNodeData, shouldInheritCommonMcpInfo } = resolveCreateNodeMcpData(
    data as { mcpInfo?: unknown; isCustomer?: boolean; },
    nodeType,
  );
  let nodeData: Record<string, any> = resolvedNodeData;
  const nodeName = typeof preferredNodeName === 'string' && preferredNodeName.trim()
    ? preferredNodeName.trim()
    : getNextNodeNameByCurrentType({
      currentNode,
      nextNodeType: nodeType,
      fallbackName: NODE_NAME_CONFIG[nodeType] ?? 'roboneo_web_node',
    });

  nodeData = {
    ...nodeData,
    name: nodeName,
  };

  const node = ctx.document.createWorkflowNode({
    id,
    type: nodeType,
    meta: {
      position,
    },
    data: nodeData,
  });

  applyToLines(ctx, id, toLines);

  // 未传入 mcpInfo 时：有预连线则按连入全局匹配 model/tool，避免公共默认模型覆盖 transmitInputParameters 的自动匹配结果
  if (shouldInheritCommonMcpInfo) {
    if (toLines?.length) {
      const latestNodeData = (node.toJSON()?.data ?? {}) as FlowNodeData;
      autoMatchAndUpdateNode(node, latestNodeData.inputNodeId);
    } else {
      applyInheritedCommonMcpInfo(node, nodeType);
    }
  }

  return node;
}

/**
 * 根据 toLines 配置，为刚创建的节点批量建立连线。
 *
 * toPortId 是目标节点的端口，当前节点自动取反向端口。
 * createLine 的 from→to 代表连线数据流方向（output→input），
 * 因此需要根据端口角色决定谁做 from、谁做 to。
 */
function applyToLines(
  ctx: FreeLayoutPluginContext,
  currentNodeId: string,
  toLines?: ToLineItem[],
) {
  if (!toLines?.length) return;
  toLines.forEach(({ toNodeId, toPortId }) => {
    const currentPort = toPortId === 'input' ? 'output' : 'input';
    if (currentPort === 'output') {
      ctx.document.linesManager.createLine({
        from: currentNodeId,
        fromPort: 'output',
        to: toNodeId,
        toPort: 'input',
      });
    } else {
      ctx.document.linesManager.createLine({
        from: toNodeId,
        fromPort: 'output',
        to: currentNodeId,
        toPort: 'input',
      });
    }
  });
}

/**
 * 在 Flow Next 画布上新增文本输入节点。
 *
 * @returns 新建节点的实体，便于后续连线等操作。
 */
export function addTextNode(ctx: FreeLayoutPluginContext, params: AddNodeParams<TextNodeData>) {
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.TEXT_NODE, params);
}

/**
 * 在 Flow Next 画布上新增图片输入节点。
 * 必须使用 {@link WorkflowDocument.createWorkflowNode}，以便触发 ADD_NODE、同步 Transform 与表单引擎；直接 `addNode` 不会走完整工作流创建链路，画布可能无变化。
 *
 * @returns 新建节点的实体，便于后续连线等操作。
 */
export function addImageNode(ctx: FreeLayoutPluginContext, params: AddNodeParams<ImageNodeData>) {
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.IMAGE_NODE, params);
}

/**
 * 在 Flow Next 画布上新增视频输入节点。
 *
 * @returns 新建节点的实体，便于后续连线等操作。
 */
export function addVideoNode(ctx: FreeLayoutPluginContext, params: AddNodeParams<VideoNodeData>) {
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.VIDEO_NODE, params);
}

/**
 * 在 Flow Next 画布上新增音频输入节点。
 *
 * @returns 新建节点的实体，便于后续连线等操作。
 */
export function addAudioNode(ctx: FreeLayoutPluginContext, params: AddNodeParams<AudioNodeData>) {
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.AUDIO_NODE, params);
}

/**
 * 在 Flow Next 画布上新增脚本输入节点。
 *
 * @returns 新建节点的实体，便于后续连线等操作。
 */
export function addScriptNode(ctx: FreeLayoutPluginContext, params: AddNodeParams<ScriptNodeData>) {
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.SCRIPT_NODE, params);
}

/**
 * 在 Flow Next 画布上新增导演台节点。
 *
 * 导演台不是模型任务节点，不进入 MCP 默认初始化集合，只写入可持久化的导演台 JSON 状态。
 * 位置继续使用节点面板传入的 x/y，避免新类型单独走默认落点。
 */
export function addDirectorConsoleNode(
  ctx: FreeLayoutPluginContext,
  params: AddNodeParams<Partial<DirectorConsoleNodeData>>,
) {
  const { smartcatTranslationText } = useLanguageStore.getState().languageStore;
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.DIRECTOR_CONSOLE, {
    ...params,
    nodeName: params.nodeName ?? smartcatTranslationText((v) => v.director_s_booth),
    data: {
      ...createDirectorConsoleDataPatch(createDefaultDirectorConsoleState()),
      ...params.data,
    },
  });
}

/**
 * 在 Flow Next 画布上新增容器节点，名称由统一计数器生成。
 */
export function addContainerNode(ctx: FreeLayoutPluginContext, params: AddNodeParams<Record<string, any>>) {
  return createWorkflowNode(ctx, TVFlowNodeNextTypeEnum.CONTAINER_NODE, params);
}

export const External_Media_Storeage_Key = 'team_flow_external_media_storage_key';

type ExternalMediaItem = {
  name: string;
  type: TVFlowNodeNextTypeEnum.IMAGE_NODE | TVFlowNodeNextTypeEnum.VIDEO_NODE | TVFlowNodeNextTypeEnum.AUDIO_NODE;
};
export type ExternalCacheDataItem =
  | (ImageMediaItem & ExternalMediaItem)
  | (VideoMediaItem & ExternalMediaItem)
  | (AudioMediaItem & ExternalMediaItem);

interface ExternalMediaNodeSize {
  width: number;
  height: number;
}

const EXTERNAL_MEDIA_NODE_GAP_Y = 48;
const EXTERNAL_AUDIO_NODE_WIDTH = 240;
const EXTERNAL_AUDIO_NODE_HEIGHT = 160;
const EXTERNAL_MEDIA_NODE_HEADER_HEIGHT = 40;
const EXTERNAL_MEDIA_NODE_FALLBACK_CONTENT_WIDTH = 360;
const EXTERNAL_MEDIA_NODE_FALLBACK_CONTENT_HEIGHT = 280;

/**
 * 添加外部资源到缓存。
 *
 * @export
 * @param {ExternalCacheDataItem[]} mediaList
 */
export function addExternalMedias(mediaList: ExternalCacheDataItem[]) {
  sessionStorage.setItem(External_Media_Storeage_Key, JSON.stringify(mediaList));
}

/**
 * 估算外部媒体节点的画布占位，和资产库插入 TeamFlow 时的避让规则保持一致。
 */
function getExternalMediaNodeSize(item: ExternalCacheDataItem): ExternalMediaNodeSize {
  if (item.type === TVFlowNodeNextTypeEnum.AUDIO_NODE) {
    return {
      width: EXTERNAL_AUDIO_NODE_WIDTH,
      height: EXTERNAL_AUDIO_NODE_HEIGHT,
    };
  }

  const rawWidth = typeof item.width === 'number' ? item.width : undefined;
  const rawHeight = typeof item.height === 'number' ? item.height : undefined;
  if (rawWidth == null || rawHeight == null || rawWidth <= 0 || rawHeight <= 0) {
    return {
      width: EXTERNAL_MEDIA_NODE_FALLBACK_CONTENT_WIDTH,
      height: EXTERNAL_MEDIA_NODE_FALLBACK_CONTENT_HEIGHT + EXTERNAL_MEDIA_NODE_HEADER_HEIGHT,
    };
  }

  const scale = Math.min(
    EXTERNAL_MEDIA_NODE_FALLBACK_CONTENT_WIDTH / rawWidth,
    EXTERNAL_MEDIA_NODE_FALLBACK_CONTENT_HEIGHT / rawHeight,
    1,
  );

  return {
    width: Math.max(1, Math.round(rawWidth * scale)),
    height: Math.max(1, Math.round(rawHeight * scale)) + EXTERNAL_MEDIA_NODE_HEADER_HEIGHT,
  };
}

/**
 * 外部媒体默认落点沿当前画布整体边界下方逐个追加，与资产库弹窗插入工作流保持一致。
 */
function getExternalMediaNodePosition(
  allNodesBounds: ReturnType<typeof getAllNodesBounds> | undefined,
  lastNodeSize: ExternalMediaNodeSize | undefined,
  lastPosition: { x: number; y: number; } | undefined,
) {
  return {
    x: allNodesBounds?.left ?? 0,
    y: lastPosition
      ? lastPosition.y + (lastNodeSize?.height ?? 0) + EXTERNAL_MEDIA_NODE_GAP_Y
      : (allNodesBounds?.bottom ?? 0) + EXTERNAL_MEDIA_NODE_GAP_Y,
  };
}

/**
 * 添加外部资源到画布，转为节点。
 * 节点位置沿当前画布整体边界下方纵向追加，与资产库弹窗插入工作流保持一致。
 *
 * @export
 * @param {FreeLayoutPluginContext} ctx
 * @return {*}
 */
export function renderExternalMedias(ctx: FreeLayoutPluginContext) {
  try {
    const cacheData = sessionStorage.getItem(External_Media_Storeage_Key);
    if (cacheData) {
      const cacheDataList: ExternalCacheDataItem[] = JSON.parse(cacheData);
      const allNodesBounds = getAllNodesBounds(ctx.document.getAllNodes());
      let lastNodeSize: ExternalMediaNodeSize | undefined;
      let lastPosition: { x: number; y: number; } | undefined;
      let lastAddedNode: ReturnType<typeof addImageNode> | undefined;

      for (let i = 0; i < cacheDataList.length; i += 1) {
        const item = cacheDataList[i];
        const { type, ...mediaItem } = item;
        const itemNodeSize = getExternalMediaNodeSize(item);
        const { x, y } = getExternalMediaNodePosition(allNodesBounds, lastNodeSize, lastPosition);
        lastPosition = { x, y };
        lastNodeSize = itemNodeSize;

        if (type === TVFlowNodeNextTypeEnum.IMAGE_NODE) {
          lastAddedNode = addImageNode(ctx, {
            x,
            y,
            data: {
              isCustomer: true,
              media_list: [mediaItem],
            },
          });
        }
        if (type === TVFlowNodeNextTypeEnum.VIDEO_NODE) {
          lastAddedNode = addVideoNode(ctx, {
            x,
            y,
            data: {
              isCustomer: true,
              media_list: [mediaItem as VideoMediaItem],
            },
          });
        }
        if (type === TVFlowNodeNextTypeEnum.AUDIO_NODE) {
          lastAddedNode = addAudioNode(ctx, {
            x,
            y,
            data: {
              isCustomer: true,
              media_list: [mediaItem as AudioMediaItem],
            },
          });
        }
      }

      if (lastAddedNode?.bounds) {
        ctx.playground.scrollToView({ bounds: lastAddedNode.bounds });
      }

      sessionStorage.removeItem(External_Media_Storeage_Key);
      return true;
    }
    return false;
  } catch (error) {
    sessionStorage.removeItem(External_Media_Storeage_Key);
    return false;
  }
}
